import { getApps, initializeApp } from "firebase/app";
import {
  Timestamp,
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  getFirestore,
  limit,
  orderBy,
  query,
  setDoc,
  updateDoc,
  where,
  writeBatch,
} from "firebase/firestore";
import { normalizeStateToAbbreviation } from "./utils/usStates";

// Copy .env.example to .env.local and replace values as needed.
// CRA exposes variables that start with REACT_APP_.
export const firebaseConfig = {
  apiKey:
    process.env.REACT_APP_FIREBASE_API_KEY ||
    "AIzaSyBl1KAPOIOpyGklXH0dpBZi9Xok8gsiIWw",
  authDomain:
    process.env.REACT_APP_FIREBASE_AUTH_DOMAIN ||
    "school-leader-ai-lab.firebaseapp.com",
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID || "school-leader-ai-lab",
  storageBucket:
    process.env.REACT_APP_FIREBASE_STORAGE_BUCKET ||
    "school-leader-ai-lab.firebasestorage.app",
  messagingSenderId:
    process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID || "1042166858118",
  appId:
    process.env.REACT_APP_FIREBASE_APP_ID ||
    "1:1042166858118:web:2acaaa8a899c6b27a4ac07",
  measurementId:
    process.env.REACT_APP_FIREBASE_MEASUREMENT_ID || "G-V2TNMP2S15",
};

const firebaseApp = getApps().length ? getApps()[0] : initializeApp(firebaseConfig);
export const db = getFirestore(firebaseApp);

const labsCollection = collection(db, "labs");
const registrationsCollection = collection(db, "registrations");
const subscribersCollection = collection(db, "subscribers");
const pollResponsesCollection = collection(db, "pollResponses");

const BATCH_WRITE_LIMIT = 350;
export const DELIVERY_VERSION = 2;
export const GENERAL_MONTHLY_LAB_KEY = "general-monthly-lab";
const generateConfirmationRequestToken = () =>
  `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

const compactPayload = (value) =>
  Object.fromEntries(Object.entries(value).filter(([, fieldValue]) => fieldValue !== undefined));

export const normalizeEmail = (value) => `${value || ""}`.trim().toLowerCase();

const normalizeLabDeliveryKey = (labId) => `${labId || GENERAL_MONTHLY_LAB_KEY}`.trim();

const normalizeIdPart = (value) =>
  `${value || ""}`
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9_-]+/g, "-")
    .replace(/^-+|-+$/g, "");

export const buildSubscriberId = (email) => normalizeEmail(email);

export const buildDeliveryRegistrationId = (labId, email) =>
  `${normalizeLabDeliveryKey(labId)}__${buildSubscriberId(email)}`;

const buildPollKey = (labId, pollId) => {
  const safeLabId = normalizeIdPart(labId);
  const safePollId = normalizeIdPart(pollId);

  if (!safeLabId || !safePollId) {
    return "";
  }

  return `${safeLabId}__${safePollId}`;
};

export const buildPollResponseId = ({ labId, pollId, clientId }) => {
  const pollKey = buildPollKey(labId, pollId);
  const safeClientId = normalizeIdPart(clientId);

  if (!pollKey || !safeClientId) {
    return "";
  }

  return `${pollKey}__${safeClientId}`;
};

const mapLabDoc = (snapshot) => {
  const data = snapshot.data();
  return {
    ...data,
    id: data.id || snapshot.id,
    status: data.status || "published",
  };
};

const mapRegistrationDoc = (snapshot) => {
  const data = snapshot.data();
  return {
    id: snapshot.id,
    ...data,
  };
};

export const getUpcomingLabs = async (maxResults = 3) => {
  const labsQuery = query(
    labsCollection,
    where("dateTime", ">=", Timestamp.now()),
    orderBy("dateTime", "asc"),
    limit(Math.max(maxResults * 4, maxResults))
  );

  const snapshot = await getDocs(labsQuery);
  return snapshot.docs
    .map(mapLabDoc)
    .filter((lab) => lab.status === "published")
    .slice(0, maxResults);
};

export const getPastLabs = async (maxResults = 20) => {
  const labsQuery = query(
    labsCollection,
    where("dateTime", "<", Timestamp.now()),
    orderBy("dateTime", "desc"),
    limit(Math.max(maxResults * 4, maxResults))
  );

  const snapshot = await getDocs(labsQuery);
  return snapshot.docs
    .map(mapLabDoc)
    .filter((lab) => lab.status === "published")
    .slice(0, maxResults);
};

export const getAllLabs = async (maxResults = 100) => {
  const labsQuery = query(labsCollection, orderBy("dateTime", "desc"), limit(maxResults));
  const snapshot = await getDocs(labsQuery);
  return snapshot.docs.map(mapLabDoc);
};

export const getLabById = async (id, options = {}) => {
  const includeUnpublished = Boolean(options.includeUnpublished);

  if (!id) {
    return null;
  }

  const snapshot = await getDoc(doc(db, "labs", id));
  if (!snapshot.exists()) {
    return null;
  }

  const lab = mapLabDoc(snapshot);
  if (!includeUnpublished && lab.status !== "published") {
    return null;
  }

  return lab;
};

// Example write helper for managing lab sessions from an admin tool or script.
export const upsertLab = async (lab) => {
  if (!lab?.id) {
    throw new Error("Lab must include an id field.");
  }

  await setDoc(doc(db, "labs", lab.id), {
    ...lab,
    id: lab.id,
    status: lab.status || "draft",
    createdAt: lab.createdAt || Timestamp.now(),
  });

  return lab.id;
};

export const archiveLab = async (labId) => {
  if (!labId) {
    throw new Error("A lab id is required to archive.");
  }

  await updateDoc(doc(db, "labs", labId), {
    status: "archived",
    archivedAt: Timestamp.now(),
  });
};

export const getSubscriberByEmail = async (email) => {
  const subscriberId = buildSubscriberId(email);
  if (!subscriberId) {
    return null;
  }

  const snapshot = await getDoc(doc(db, "subscribers", subscriberId));
  if (!snapshot.exists()) {
    return null;
  }

  return {
    id: snapshot.id,
    ...snapshot.data(),
  };
};

export const upsertSubscriber = async ({
  firstName,
  email,
  role,
  state,
  sourceType,
  sourceSlug,
  sourceName,
  active = true,
}) => {
  const normalizedEmail = buildSubscriberId(email);
  if (!normalizedEmail) {
    throw new Error("A valid email is required.");
  }

  const now = Timestamp.now();
  const subscriberRef = doc(db, "subscribers", normalizedEmail);
  const existingSnapshot = await getDoc(subscriberRef);
  const existing = existingSnapshot.exists() ? existingSnapshot.data() : null;
  const nextActive = active !== false;
  const wasInactive = existing?.active === false;

  const payload = compactPayload({
    id: normalizedEmail,
    firstName: `${firstName || ""}`.trim(),
    email: normalizedEmail,
    role: `${role || ""}`.trim(),
    state: normalizeStateToAbbreviation(state),
    sourceType: sourceType || "public",
    sourceSlug: sourceSlug || "main-hub",
    sourceName: sourceName || "AI Lab Hub",
    firstSourceType: existing?.firstSourceType || sourceType || "public",
    firstSourceSlug: existing?.firstSourceSlug || sourceSlug || "main-hub",
    firstSourceName: existing?.firstSourceName || sourceName || "AI Lab Hub",
    active: nextActive,
    subscribedAt: existing?.subscribedAt || now,
    unsubscribedAt: nextActive
      ? null
      : existing?.active === false
      ? existing?.unsubscribedAt || now
      : now,
    resubscribedAt: nextActive && wasInactive ? now : existing?.resubscribedAt,
    lastSeenAt: now,
    updatedAt: now,
  });

  await setDoc(subscriberRef, payload, { merge: true });

  return {
    ...(existing || {}),
    ...payload,
  };
};

export const upsertLabDeliveryRegistration = async ({
  firstName,
  email,
  role,
  state,
  labId,
  sourceType,
  sourceSlug,
  sourceName,
  requestConfirmation = true,
}) => {
  const normalizedEmail = buildSubscriberId(email);
  if (!normalizedEmail) {
    throw new Error("A valid email is required.");
  }

  if (!labId) {
    return {
      registrationId: null,
      alreadyRegistered: false,
      registration: null,
    };
  }

  const registrationId = buildDeliveryRegistrationId(labId, normalizedEmail);
  const registrationRef = doc(db, "registrations", registrationId);
  const existingSnapshot = await getDoc(registrationRef);
  const existing = existingSnapshot.exists() ? existingSnapshot.data() : null;
  const now = Timestamp.now();
  const confirmationRequestToken = requestConfirmation
    ? generateConfirmationRequestToken()
    : undefined;

  const basePayload = compactPayload({
    id: registrationId,
    firstName: `${firstName || ""}`.trim(),
    email: normalizedEmail,
    role: `${role || ""}`.trim(),
    state: normalizeStateToAbbreviation(state),
    labId: labId || null,
    sourceType: sourceType || "public",
    sourceSlug: sourceSlug || "main-hub",
    sourceName: sourceName || "AI Lab Hub",
    subscriberId: normalizedEmail,
    deliveryVersion: DELIVERY_VERSION,
    deliveryActive: true,
    confirmationRequestedAt: requestConfirmation ? now : undefined,
    confirmationRequestToken,
    updatedAt: now,
  });

  if (!existingSnapshot.exists()) {
    await setDoc(registrationRef, {
      ...basePayload,
      reminder14dSent: false,
      reminder7dSent: false,
      reminder3dSent: false,
      reminder24hSent: false,
      reminder1hSent: false,
      reminderStartSent: false,
      confirmationSent: false,
      createdAt: now,
    });

    return {
      registrationId,
      alreadyRegistered: false,
      registration: {
        ...basePayload,
        createdAt: now,
      },
    };
  }

  await setDoc(registrationRef, basePayload, { merge: true });

  return {
    registrationId,
    alreadyRegistered: existing?.deliveryActive === true,
    registration: {
      ...existing,
      ...basePayload,
    },
  };
};

export const markRegistrationConfirmationSent = async (registrationId) => {
  if (!registrationId) {
    return;
  }

  await updateDoc(doc(db, "registrations", registrationId), {
    confirmationSent: true,
    confirmationSentAt: Timestamp.now(),
    confirmationError: "",
    updatedAt: Timestamp.now(),
  });
};

const updateRegistrationRefsInBatches = async (references, payload) => {
  if (references.length === 0) {
    return 0;
  }

  let updatedCount = 0;
  let batch = writeBatch(db);
  let writeCount = 0;

  for (const reference of references) {
    batch.update(reference, payload);
    writeCount += 1;
    updatedCount += 1;

    if (writeCount >= BATCH_WRITE_LIMIT) {
      await batch.commit();
      batch = writeBatch(db);
      writeCount = 0;
    }
  }

  if (writeCount > 0) {
    await batch.commit();
  }

  return updatedCount;
};

export const setSubscriberActiveByEmail = async ({ email, active }) => {
  const normalizedEmail = buildSubscriberId(email);
  if (!normalizedEmail) {
    throw new Error("A valid email is required.");
  }

  const subscriberRef = doc(db, "subscribers", normalizedEmail);
  const subscriberSnapshot = await getDoc(subscriberRef);
  const existingSubscriber = subscriberSnapshot.exists() ? subscriberSnapshot.data() : null;
  const nextActive = active !== false;
  const now = Timestamp.now();
  const unsubscribedAt =
    nextActive
      ? null
      : existingSubscriber?.active === false
      ? existingSubscriber?.unsubscribedAt || now
      : now;

  const subscriberPayload = compactPayload({
    id: normalizedEmail,
    email: normalizedEmail,
    active: nextActive,
    unsubscribedAt,
    resubscribedAt:
      nextActive && existingSubscriber?.active === false
        ? now
        : existingSubscriber?.resubscribedAt,
    updatedAt: now,
    lastSeenAt: now,
  });

  await setDoc(subscriberRef, subscriberPayload, { merge: true });

  const [subscriberRegistrationsSnapshot, legacyRegistrationsSnapshot] = await Promise.all([
    getDocs(
      query(
        registrationsCollection,
        where("subscriberId", "==", normalizedEmail),
        limit(5000)
      )
    ),
    getDocs(query(registrationsCollection, where("email", "==", normalizedEmail), limit(5000))),
  ]);

  const referenceByPath = new Map();
  subscriberRegistrationsSnapshot.docs.forEach((registrationDoc) => {
    referenceByPath.set(registrationDoc.ref.path, registrationDoc.ref);
  });
  legacyRegistrationsSnapshot.docs.forEach((registrationDoc) => {
    referenceByPath.set(registrationDoc.ref.path, registrationDoc.ref);
  });

  const registrationUpdatePayload = {
    deliveryActive: nextActive,
    unsubscribedAt,
    updatedAt: now,
  };

  const updatedRegistrations = await updateRegistrationRefsInBatches(
    Array.from(referenceByPath.values()),
    registrationUpdatePayload
  );

  return {
    subscriberId: normalizedEmail,
    active: nextActive,
    updatedRegistrations,
  };
};

export const createRegistration = async ({
  firstName,
  email,
  role,
  state,
  labId,
  sourceType,
  sourceSlug,
  sourceName,
}) => {
  const subscriber = await upsertSubscriber({
    firstName,
    email,
    role,
    state,
    sourceType,
    sourceSlug,
    sourceName,
    active: true,
  });

  const delivery = await upsertLabDeliveryRegistration({
    firstName: subscriber.firstName || firstName,
    email: subscriber.email || email,
    role: subscriber.role || role,
    state: subscriber.state || state,
    labId,
    sourceType: subscriber.sourceType || sourceType,
    sourceSlug: subscriber.sourceSlug || sourceSlug,
    sourceName: subscriber.sourceName || sourceName,
  });

  return {
    subscriber,
    ...delivery,
  };
};

export const backfillSubscribersFromLegacyRegistrations = async (maxRecords = 10000) => {
  const safeLimit = Math.max(1, Math.min(Number(maxRecords) || 10000, 25000));
  const migrationTime = Timestamp.now();
  let snapshot = null;

  try {
    const orderedQuery = query(
      registrationsCollection,
      orderBy("createdAt", "asc"),
      limit(safeLimit)
    );
    snapshot = await getDocs(orderedQuery);
  } catch (error) {
    console.warn("Falling back to unordered backfill query:", error);
    const fallbackQuery = query(registrationsCollection, limit(safeLimit));
    snapshot = await getDocs(fallbackQuery);
  }

  if (snapshot.empty) {
    return {
      processed: 0,
      subscribersUpserted: 0,
      deliveriesUpserted: 0,
      legacyMarked: 0,
      skipped: 0,
    };
  }

  let processed = 0;
  let subscribersUpserted = 0;
  let deliveriesUpserted = 0;
  let legacyMarked = 0;
  let skipped = 0;
  let batch = writeBatch(db);
  let writeCount = 0;

  const commitBatch = async () => {
    if (writeCount === 0) {
      return;
    }

    await batch.commit();
    batch = writeBatch(db);
    writeCount = 0;
  };

  for (const registrationSnapshot of snapshot.docs) {
    processed += 1;
    const legacy = registrationSnapshot.data();
    const normalizedEmail = buildSubscriberId(legacy.email);

    if (!normalizedEmail) {
      skipped += 1;
      continue;
    }

    const subscriberId = buildSubscriberId(normalizedEmail);
    const subscriberRef = doc(db, "subscribers", subscriberId);
    const subscriberPayload = compactPayload({
      id: subscriberId,
      email: normalizedEmail,
      firstName: `${legacy.firstName || ""}`.trim(),
      role: `${legacy.role || ""}`.trim(),
      state: normalizeStateToAbbreviation(legacy.state),
      sourceType: legacy.sourceType || "public",
      sourceSlug: legacy.sourceSlug || "main-hub",
      sourceName: legacy.sourceName || "AI Lab Hub",
      firstSourceType: legacy.sourceType || "public",
      firstSourceSlug: legacy.sourceSlug || "main-hub",
      firstSourceName: legacy.sourceName || "AI Lab Hub",
      active: legacy.deliveryActive !== false,
      subscribedAt: legacy.createdAt || migrationTime,
      lastSeenAt: migrationTime,
      updatedAt: migrationTime,
    });

    batch.set(subscriberRef, subscriberPayload, { merge: true });
    writeCount += 1;
    subscribersUpserted += 1;

    if (legacy.labId) {
      const deliveryId = buildDeliveryRegistrationId(legacy.labId, normalizedEmail);
      const deliveryRef = doc(db, "registrations", deliveryId);
      const deliveryPayload = compactPayload({
        id: deliveryId,
        firstName: `${legacy.firstName || ""}`.trim(),
        email: normalizedEmail,
        role: `${legacy.role || ""}`.trim(),
        state: normalizeStateToAbbreviation(legacy.state),
        labId: legacy.labId || null,
        sourceType: legacy.sourceType || "public",
        sourceSlug: legacy.sourceSlug || "main-hub",
        sourceName: legacy.sourceName || "AI Lab Hub",
        subscriberId,
        deliveryVersion: DELIVERY_VERSION,
        deliveryActive: legacy.deliveryActive !== false,
        createdAt: legacy.createdAt || migrationTime,
        updatedAt: migrationTime,
        reminder24hSent: legacy.reminder24hSent === true ? true : undefined,
        reminder24hSentAt:
          legacy.reminder24hSent === true ? legacy.reminder24hSentAt : undefined,
        reminder1hSent: legacy.reminder1hSent === true ? true : undefined,
        reminder1hSentAt: legacy.reminder1hSent === true ? legacy.reminder1hSentAt : undefined,
        reminderStartSent: legacy.reminderStartSent === true ? true : undefined,
        reminderStartSentAt:
          legacy.reminderStartSent === true ? legacy.reminderStartSentAt : undefined,
        confirmationSent: legacy.confirmationSent === true ? true : undefined,
        confirmationSentAt:
          legacy.confirmationSent === true ? legacy.confirmationSentAt : undefined,
        lastConfirmationResentAt: legacy.lastConfirmationResentAt,
        notes: legacy.notes,
        tags: Array.isArray(legacy.tags) ? legacy.tags : undefined,
      });

      batch.set(deliveryRef, deliveryPayload, { merge: true });
      writeCount += 1;
      deliveriesUpserted += 1;

      if (registrationSnapshot.id !== deliveryId) {
        batch.set(
          registrationSnapshot.ref,
          {
            migratedToDeliveryId: deliveryId,
            migratedToDeliveryVersion: DELIVERY_VERSION,
            migratedSubscriberId: subscriberId,
            migrationBackfilledAt: migrationTime,
          },
          { merge: true }
        );
        writeCount += 1;
        legacyMarked += 1;
      }
    } else {
      batch.set(
        registrationSnapshot.ref,
        {
          migratedSubscriberId: subscriberId,
          migrationBackfilledAt: migrationTime,
        },
        { merge: true }
      );
      writeCount += 1;
      legacyMarked += 1;
    }

    if (writeCount >= BATCH_WRITE_LIMIT) {
      await commitBatch();
    }
  }

  await commitBatch();

  return {
    processed,
    subscribersUpserted,
    deliveriesUpserted,
    legacyMarked,
    skipped,
  };
};

export const getAllRegistrations = async (maxResults = 2000) => {
  const safeLimit = Math.max(1, maxResults);

  try {
    const v2RegistrationsQuery = query(
      registrationsCollection,
      where("deliveryVersion", "==", DELIVERY_VERSION),
      orderBy("createdAt", "desc"),
      limit(safeLimit)
    );
    const v2Snapshot = await getDocs(v2RegistrationsQuery);

    if (!v2Snapshot.empty) {
      return v2Snapshot.docs.map(mapRegistrationDoc);
    }
  } catch (error) {
    console.warn("Falling back to legacy registrations query:", error);
  }

  const fallbackQuery = query(
    registrationsCollection,
    orderBy("createdAt", "desc"),
    limit(safeLimit)
  );
  const fallbackSnapshot = await getDocs(fallbackQuery);
  return fallbackSnapshot.docs.map(mapRegistrationDoc);
};

export const updateRegistration = async (registrationId, updates) => {
  if (!registrationId) {
    throw new Error("A registration id is required.");
  }

  const payload = Object.fromEntries(
    Object.entries(updates || {}).filter(([, value]) => value !== undefined)
  );

  await updateDoc(doc(db, "registrations", registrationId), {
    ...payload,
    updatedAt: Timestamp.now(),
  });
};

export const deleteRegistration = async (registrationId) => {
  if (!registrationId) {
    throw new Error("A registration id is required.");
  }

  await deleteDoc(doc(db, "registrations", registrationId));
};

export const findRegistrationByEmailAndLab = async ({ email, labId }) => {
  const normalizedEmail = buildSubscriberId(email);
  if (!normalizedEmail || !labId) {
    return null;
  }

  const deterministicId = buildDeliveryRegistrationId(labId, normalizedEmail);
  const deterministicSnapshot = await getDoc(doc(db, "registrations", deterministicId));

  if (deterministicSnapshot.exists()) {
    return {
      id: deterministicSnapshot.id,
      ...deterministicSnapshot.data(),
    };
  }

  const registrationsQuery = query(
    registrationsCollection,
    where("email", "==", normalizedEmail),
    where("labId", "==", labId),
    limit(1)
  );
  const snapshot = await getDocs(registrationsQuery);

  if (snapshot.empty) {
    return null;
  }

  const registrationDoc = snapshot.docs[0];
  return {
    id: registrationDoc.id,
    ...registrationDoc.data(),
  };
};

export const createQuestion = async ({ name, email, role, state, question }) => {
  const now = Timestamp.now();
  const payload = {
    name: name || "",
    email: email || "",
    role: role || "",
    state: state || "",
    question,
    createdAt: now,
    updatedAt: now,
  };

  return addDoc(collection(db, "questions"), payload);
};

export const getLabPollResponseByClient = async ({ labId, pollId, clientId }) => {
  const responseId = buildPollResponseId({ labId, pollId, clientId });
  if (!responseId) {
    return null;
  }

  const snapshot = await getDoc(doc(db, "pollResponses", responseId));
  if (!snapshot.exists()) {
    return null;
  }

  return {
    id: snapshot.id,
    ...snapshot.data(),
  };
};

export const submitLabPollResponse = async ({ labId, pollId, clientId, answer }) => {
  const responseId = buildPollResponseId({ labId, pollId, clientId });
  const pollKey = buildPollKey(labId, pollId);
  const normalizedAnswer = `${answer || ""}`.trim();
  const normalizedClientId = normalizeIdPart(clientId);

  if (!responseId || !pollKey || !normalizedAnswer || !normalizedClientId) {
    throw new Error("A valid lab, poll, browser id, and answer are required.");
  }

  const responseRef = doc(db, "pollResponses", responseId);
  const existing = await getDoc(responseRef);
  if (existing.exists()) {
    const error = new Error("already-submitted");
    error.code = "already-submitted";
    throw error;
  }

  const now = Timestamp.now();
  const payload = {
    id: responseId,
    pollKey,
    labId,
    pollId,
    clientId: normalizedClientId,
    answer: normalizedAnswer,
    createdAt: now,
    updatedAt: now,
  };

  await setDoc(responseRef, payload);
  return payload;
};

const timestampToMs = (value) => {
  if (!value) {
    return 0;
  }

  if (typeof value.toDate === "function") {
    return value.toDate().getTime();
  }

  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? 0 : parsed.getTime();
};

export const getLabPollResponses = async ({ labId, pollId, maxResults = 500 }) => {
  const pollKey = buildPollKey(labId, pollId);
  if (!pollKey) {
    return [];
  }

  const snapshot = await getDocs(
    query(pollResponsesCollection, where("pollKey", "==", pollKey), limit(maxResults))
  );

  return snapshot.docs
    .map((entry) => ({
      id: entry.id,
      ...entry.data(),
    }))
    .sort((left, right) => timestampToMs(right.createdAt) - timestampToMs(left.createdAt));
};
